import { PopoverPlacement } from "@idui/react-popover";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { IconButton } from "@mui/material";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { FramerTooltip } from "@ryde/controls";
import {
  drawersAtom,
  RydeUiUnit,
  useOpenRelatedEntitiesDrawer,
} from "@ryde/shared";

import { RELATED_ENTITIES_HEADER_ICON_SIZE_PX } from "./related-entities.constants";
import { RelatedEntitiesHeaderIconStyled } from "./related-entities.styled";
import { getShowRelatedEntitiesTooltipText } from "./related-entities.utils";

interface IRelatedEntitiesHeaderIcon {
  entity: RydeUiUnit;
  entityId?: number;
  entityLabel?: string;
  drawerNumber?: number;
}

export const RelatedEntitiesHeaderIcon: FC<IRelatedEntitiesHeaderIcon> = ({
  entity,
  entityId,
  entityLabel,
  drawerNumber,
}) => {
  const { t } = useTranslation();
  const drawers = useRecoilValue(drawersAtom);
  const { openRelatedEntitiesDrawer, isEntityRelationsSupported } =
    useOpenRelatedEntitiesDrawer();

  const tooltipText = useMemo(
    () =>
      entityId === undefined
        ? t("general.SHOW_RELATED_ENTITIES")
        : getShowRelatedEntitiesTooltipText({ t, entity, entityId }),
    [entity, entityId, t],
  );

  const handleClick = useEvent(() => {
    openRelatedEntitiesDrawer({ entity, entityId, entityLabel });
  });

  const isObscuredByStackedDrawer =
    drawerNumber === undefined
      ? drawers.length > 0
      : drawerNumber < drawers.length - 1;

  if (
    !entityId ||
    !isEntityRelationsSupported(entity) ||
    isObscuredByStackedDrawer
  ) {
    return null;
  }

  return (
    <RelatedEntitiesHeaderIconStyled
      className="related-entities-header-icon-wrapper"
      $alwaysVisible
    >
      <FramerTooltip content={tooltipText} placement={PopoverPlacement.bottom}>
        <IconButton
          size="small"
          className="related-entities-header-button"
          onClick={handleClick}
          aria-label={tooltipText}
        >
          <AccountTreeOutlinedIcon
            sx={{ fontSize: RELATED_ENTITIES_HEADER_ICON_SIZE_PX }}
          />
        </IconButton>
      </FramerTooltip>
    </RelatedEntitiesHeaderIconStyled>
  );
};
